import { useCallback, useMemo } from 'react';

interface IUseCheckboxes<T extends Record<string, any>> {
  data: T[];
  checkedItemIds: Set<string | number>;
  setCheckedItemIds: (data: Set<string | number>) => void;
  key?: keyof T;
}

function useCheckboxes<T extends Record<string, any>>({
  data,
  checkedItemIds,
  setCheckedItemIds,
  key = 'id',
}: IUseCheckboxes<T>) {
  const allItemsAreChecked = useMemo(
    () => data.every((item) => checkedItemIds.has(item[key])),
    [data, checkedItemIds, key]
  );

  const someItemsAreChecked = useMemo(
    () => data.some((item) => checkedItemIds.has(item[key])),
    [data, checkedItemIds, key]
  );

  const handleCheck = useCallback(
    (checked: boolean, id: string): void => {
      const newCheckedItemIds = new Set(checkedItemIds);

      if (checked) {
        newCheckedItemIds.add(id);
      } else {
        newCheckedItemIds.delete(id);
      }

      setCheckedItemIds(newCheckedItemIds);
    },
    [checkedItemIds, setCheckedItemIds]
  );

  const handleCheckAll = useCallback(() => {
    const newCheckedItemIds = new Set(checkedItemIds);

    if (someItemsAreChecked) {
      data.forEach((row) => newCheckedItemIds.delete(row[key]));
    } else {
      data.forEach((row) => {
        newCheckedItemIds.add(row[key]);
      });
    }

    setCheckedItemIds(newCheckedItemIds);
  }, [checkedItemIds, data, key, setCheckedItemIds, someItemsAreChecked]);

  return {
    allItemsAreChecked,
    someItemsAreChecked,
    handleCheck,
    handleCheckAll,
  };
}

export default useCheckboxes;
